// Single source of truth for the known Cluey observation types used across
// Focus: result-list/modal icon + label, the mapbox marker sprite match
// expression, and the "hidden tag" check that stops a result chip from
// repeating the observation type itself.
//
// Cluey's raw `observationType` / `EntityType` values differ by source and
// by history (spaces, underscores or abbreviations - e.g. "hwc",
// "human wildlife conflict" and "human_wildlife_conflict" are all the same
// type), so every consumer normalises the raw value before matching against
// the definitions below. Add a new observation type here once, rather than
// touching every file that renders an icon, label or map marker.

const normalizeTypeKey = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_\- ]/g, '');

// One entry per known observation type.
// - `variants`: every raw string seen in `observationType` / `EntityType`
//   fields for this type (spaced, underscored and abbreviated forms).
// - `spriteName`: the mapbox sprite image name for this type's map marker.
// - `icon` / `label`: used by the result-list card and observation modal.
const OBSERVATION_TYPES = [
  {
    key: 'animalsighting',
    variants: [
      'animal',
      'animalsighting',
      'animal sighting',
      'animal_sighting'
    ],
    icon: './assets/img/icons/icon_animal.svg',
    label: 'Animal sighting',
    spriteName: 'animalsighting'
  },
  {
    key: 'infrastructure',
    variants: [
      'infrastructure',
      'pointofinterest',
      'poi',
      'Point of Interest',
      'point of interest',
      'point_of_interest'
    ],
    icon: './assets/img/icons/icon_infrastructure.svg',
    label: 'Point of interest',
    spriteName: 'infrastructure'
  },
  {
    key: 'hwc',
    variants: [
      'hwc',
      'humanwildlifeconflict',
      'human wildlife conflict',
      'human_wildlife_conflict'
    ],
    icon: './assets/img/icons/icon_hwc.svg',
    label: 'Human wildlife conflict',
    spriteName: 'hwc'
  },
  {
    key: 'humanactivity',
    variants: ['offence', 'humanactivity', 'human activity', 'human_activity'],
    icon: './assets/img/icons/icon_humanactivity.svg',
    label: 'Human activity',
    spriteName: 'humanactivity'
  },
  {
    key: 'community',
    variants: [
      'community',
      'communitywork',
      'community work',
      'community_work'
    ],
    icon: './assets/img/icons/icon_community.svg',
    label: 'Community work',
    spriteName: 'community'
  },
  {
    key: 'wash',
    variants: ['wash', 'WASH'],
    icon: './assets/img/icons/icon_wash.svg',
    label: 'WASH',
    spriteName: 'WASH'
  }
];

// Raw values that resolve to a "no icon" special label rather than a real
// observation type - only used in the result list / modal, never on the map.
const SPECIAL_LABELS = {
  afind: 'A find'
};

// Raw `EntityType` values for photo attachments on the map. These use their
// own sprite rather than the parent observation type's sprite, so they are
// kept separate from OBSERVATION_TYPES.
const PHOTO_SPRITE_VARIANTS = ['animalsighting_photo', 'humanactivity_photo'];

// Matches a raw observationType/EntityType value against every known
// variant (not just the canonical `key`) - e.g. "Point_of_interest" and
// "poi" both need to resolve to the `infrastructure` type, even though
// neither literally equals its `key`.
const findType = observationTypeRaw => {
  const key = normalizeTypeKey(observationTypeRaw);
  return (
    OBSERVATION_TYPES.find(type =>
      type.variants.some(variant => normalizeTypeKey(variant) === key)
    ) || null
  );
};

// Icon path for the result-list card / modal header. Returns '' when the
// type is unknown or has no icon (e.g. "A find").
export const getObservationTypeIcon = observationTypeRaw => {
  const type = findType(observationTypeRaw);
  return type ? type.icon : '';
};

// Human-readable label. Falls back to a capitalised version of the raw
// value, or "Linked observation" when there is no raw value at all.
export const getObservationTypeLabel = observationTypeRaw => {
  const type = findType(observationTypeRaw);
  if (type) return type.label;
  const key = normalizeTypeKey(observationTypeRaw);
  if (SPECIAL_LABELS[key]) return SPECIAL_LABELS[key];
  const raw = (observationTypeRaw || '').toString().trim();
  return raw
    ? raw.charAt(0).toUpperCase() + raw.slice(1)
    : 'Linked observation';
};

// Icon + alt text + label bundle used by the result-list card, including
// the "A find" special case and the generic/unknown-type fallback.
export const getObservationTypeMeta = observationTypeRaw => {
  const type = findType(observationTypeRaw);
  if (type) {
    return { icon: type.icon, iconAlt: type.label, label: type.label };
  }
  const key = normalizeTypeKey(observationTypeRaw);
  if (SPECIAL_LABELS[key]) {
    return { icon: null, iconAlt: '', label: SPECIAL_LABELS[key] };
  }
  const raw = (observationTypeRaw || '').toString().trim();
  return {
    icon: null,
    iconAlt: '',
    label: raw
      ? raw.charAt(0).toUpperCase() + raw.slice(1)
      : 'Linked observation'
  };
};

// Whether `observationTypeRaw` matches one of the known types above (used
// to decide whether a "generic type" fallback should be rendered instead).
export const isKnownObservationType = observationTypeRaw =>
  Boolean(findType(observationTypeRaw)) ||
  Boolean(SPECIAL_LABELS[normalizeTypeKey(observationTypeRaw)]);

// Set of normalised tag/concept labels that duplicate the observation type
// itself (or are otherwise never useful as a result chip), so they can be
// filtered out of the tag/concept chip list.
const HIDDEN_RESULT_TAGS = new Set([
  'alert',
  'note',
  'humanactivity',
  'pointofinterest',
  'poi',
  'animalsighting',
  'humanwildlifeconflict',
  'communitywork',
  'wash'
]);

// Normalises an arbitrary tag/concept label for comparison against
// HIDDEN_RESULT_TAGS. Deliberately broader than the observation-type key
// normaliser above (it strips *all* non-alphanumeric characters, not just
// underscores/dashes/spaces) because tag/concept text is free-form, unlike
// the fixed observationType/EntityType vocabulary.
export const normalizeTagLabel = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

export const shouldShowResultTag = value =>
  !HIDDEN_RESULT_TAGS.has(normalizeTagLabel(value));

// Builds the mapbox `['match', ['get', 'EntityType'], ...]` expression used
// to assign marker sprite icons on the map. Pass `includePhotoVariants: true`
// for layers that also render photo-attachment markers.
export const getObservationSpriteMatchExpression = ({
  includePhotoVariants = false
} = {}) => {
  const expression = ['match', ['get', 'EntityType']];
  OBSERVATION_TYPES.forEach(type => {
    type.variants.forEach(variant => {
      expression.push([variant], type.spriteName);
    });
  });
  if (includePhotoVariants) {
    PHOTO_SPRITE_VARIANTS.forEach(variant => {
      expression.push([variant], variant);
    });
  }
  expression.push('');
  return expression;
};

// Builds the mapbox `['match', ['get', 'EntityType'], ...]` expression used
// on the "_clicked" marker layer, where both the `_clicked` and `_selected`
// raw suffixes resolve to the sprite's `_clicked` variant (there is no
// separate "selected" sprite - clicking or selecting a marker shows the
// same highlighted icon). Also maps the legacy `cluster_clicked` value to
// the community sprite, matching the previous per-layer expressions.
export const getObservationClickedSpriteMatchExpression = () => {
  const expression = ['match', ['get', 'EntityType']];
  OBSERVATION_TYPES.forEach(type => {
    type.variants.forEach(variant => {
      expression.push([`${variant}_clicked`], `${type.spriteName}_clicked`);
      expression.push([`${variant}_selected`], `${type.spriteName}_clicked`);
    });
  });
  expression.push(['cluster_clicked'], 'community_clicked');
  expression.push('');
  return expression;
};
